import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Loader2, SlidersHorizontal, Database } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [s, setS] = useState(null);
  const [savingS, setSavingS] = useState(false);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (settings) setS(settings); }, [settings]);

  const saveSettings = async () => {
    setSavingS(true);
    try {
      await api.put("/settings", s);
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSavingS(false); }
  };

  const runDemo = async (action) => {
    setBusy(true);
    try {
      await api.post(`/demo/${action}`);
      toast.success(action === "reset" ? "Demo data deleted" : "Demo data seeded");
      ["clients", "content", "shoots", "payments", "dashboard", "tasks", "publishing", "team"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!s) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Settings</h1>
        <p className="mt-1 text-sm text-zinc-500">Weekly health thresholds & demo data</p>
      </div>

      <Card className="rounded-lg border-zinc-200 shadow-sm">
        <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4">
          <SlidersHorizontal className="h-4 w-4 text-zinc-400" />
          <h3 className="font-heading text-base font-semibold">Weekly Health Thresholds</h3>
        </div>
        <div className="p-5">
          <p className="mb-4 text-sm text-zinc-500">Health compares actual posted progress against the week's expected progress (Mon = 0%, Sat = 100%). Gap = actual − expected. Monday mornings stay green automatically.</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label className="text-xs text-zinc-500">On Track when gap ≥</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input type="number" step="0.05" value={s.on_track_gap} onChange={(e) => setS({ ...s, on_track_gap: Number(e.target.value) })} data-testid="on-track-input" />
                <span className="text-sm text-zinc-400">({Math.round(s.on_track_gap * 100)}%)</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Needs Attention when gap ≥</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input type="number" step="0.05" value={s.attention_gap} onChange={(e) => setS({ ...s, attention_gap: Number(e.target.value) })} data-testid="attention-input" />
                <span className="text-sm text-zinc-400">({Math.round(s.attention_gap * 100)}%)</span>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Payment "Due Soon" window (days)</Label>
              <Input type="number" value={s.due_soon_days} onChange={(e) => setS({ ...s, due_soon_days: Number(e.target.value) })} className="mt-1" data-testid="due-soon-input" />
            </div>
          </div>
          <Button className="mt-4" onClick={saveSettings} disabled={savingS} data-testid="save-settings-button">{savingS && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Thresholds</Button>
        </div>
      </Card>

      <Card className="rounded-lg border-zinc-200 shadow-sm">
        <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4"><Database className="h-4 w-4 text-zinc-400" /><h3 className="font-heading text-base font-semibold">Demo Data</h3></div>
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <p className="text-sm text-zinc-500">Reset removes all seeded demo data. Your real accounts are untouched.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => runDemo("seed")} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Re-seed Demo</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="outline" className="text-red-600" data-testid="delete-demo-button">Delete Demo Data</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete all demo data?</AlertDialogTitle>
                  <AlertDialogDescription>This permanently removes seeded demo clients, content, tasks, stories, shoots, payments and demo team members.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => runDemo("reset")} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </Card>
    </div>
  );
}
